"use client";

/**
 * PasswordStrengthIndicator
 * REQ-029: Account Settings - Password strength visual meter
 *
 * Displays a visual indicator of password strength based on complexity rules:
 * - Length (8+ chars)
 * - Uppercase letter
 * - Lowercase letter
 * - Number
 * - Special character (bonus)
 */

import * as React from "react";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";

export interface PasswordStrengthIndicatorProps {
  /** The password to evaluate */
  password: string;
  /** Test ID for E2E testing */
  "data-testid"?: string;
}

interface StrengthResult {
  score: number;
  label: string;
  color: "error" | "warning" | "info" | "success";
}

/**
 * Evaluate password strength on a 0-100 scale.
 */
export function evaluatePasswordStrength(password: string): StrengthResult {
  if (!password) {
    return { score: 0, label: "", color: "error" };
  }

  let score = 0;

  // Length checks
  if (password.length >= 8) score += 25;
  if (password.length >= 12) score += 10;

  // Character type checks
  if (/[A-Z]/.test(password)) score += 20;
  if (/[a-z]/.test(password)) score += 20;
  if (/[0-9]/.test(password)) score += 15;
  if (/[^A-Za-z0-9]/.test(password)) score += 10;

  // Cap at 100
  score = Math.min(score, 100);

  if (score < 30) {
    return { score, label: "Weak", color: "error" };
  }
  if (score < 60) {
    return { score, label: "Fair", color: "warning" };
  }
  if (score < 80) {
    return { score, label: "Good", color: "info" };
  }
  return { score, label: "Strong", color: "success" };
}

/**
 * PasswordStrengthIndicator Component
 *
 * Visual meter that shows password complexity as a progress bar with label.
 *
 * @example
 * ```tsx
 * <PasswordStrengthIndicator password={watchedPassword} />
 * ```
 */
export function PasswordStrengthIndicator({
  password,
  "data-testid": testId = "password-strength-indicator",
}: PasswordStrengthIndicatorProps): React.JSX.Element | null {
  const { score, label, color } = evaluatePasswordStrength(password);

  if (!password) {
    return null;
  }

  return (
    <Box data-testid={testId} sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          Password strength
        </Typography>
        <Typography
          variant="caption"
          data-testid={`${testId}-label`}
          sx={{
            color: `${color}.main`,
            fontWeight: 600,
          }}
        >
          {label}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={score}
        color={color}
        data-testid={`${testId}-bar`}
        sx={{ height: 6, borderRadius: 3 }}
      />
    </Box>
  );
}
